import { watch } from "@arcgis/core/core/reactiveUtils";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import MapView from "@arcgis/core/views/MapView";
import {
  getAnyLayerGraphic,
  getPopupPlacement,
  zoomIntoCluster,
} from "@components/world-map/helpers/world-map-popup-helpers";
import {
  SelectedMarkerPopup,
  UseWorldMapPopupsProps,
  UseWorldMapPopupsReturn,
} from "@components/world-map/types/world-map-popup-types";
import { WorldMapEntity } from "@components/world-map/world-map-types";
import { isClusterGraphic } from "@components/world-map/world-map-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";

/**
 * Manages the marker popup lifecycle for the world map.
 * Handles click-to-open, pointer cursor updates, and popup repositioning during pan/zoom.
 */
export function useWorldMapPopups<T extends WorldMapEntity>({
  viewRef,
  layerRef,
  isViewReady,
  entities,
  isPopupEnabled,
  onClusterClick,
  onMapClick,
}: UseWorldMapPopupsProps<T>): UseWorldMapPopupsReturn<T> {
  const { handleErrorWithToast } = useErrorContext();
  const [selectedMarkerPopup, setSelectedMarkerPopup] =
    useState<SelectedMarkerPopup<T> | null>(null);

  const isPointerHitTestInFlight = useRef(false);
  const shouldRunPointerHitTestAgain = useRef(false);
  const latestPointerScreenPoint = useRef<__esri.ScreenPoint | null>(null);
  const pointerMoveFrameHandle = useRef<number | null>(null);
  const markerClickHandle = useRef<ReturnType<MapView["on"]> | null>(null);
  const markerPointerMoveHandle = useRef<ReturnType<MapView["on"]> | null>(
    null
  );
  const extentWatchHandle = useRef<ReturnType<MapView["watch"]> | null>(null);

  const entitiesById = useMemo(() => {
    return new Map(entities.map((entity) => [entity.id, entity]));
  }, [entities]);

  /**
   * Handles map click and opens/closes marker popup.
   * When a cluster is clicked, zooms in to reveal individual markers.
   */
  const handleMapClick = useEffectEvent(
    async (event: __esri.ViewClickEvent): Promise<void> => {
      try {
        event.stopPropagation();
        if (!viewRef.current) {
          return;
        }

        setSelectedMarkerPopup(null);

        const graphic = await getAnyLayerGraphic(
          viewRef.current,
          layerRef.current,
          event
        );

        // If click is not on a marker or cluster, trigger optional onMapClick callback and return early.
        if (!graphic) {
          onMapClick?.(event);
          return;
        }

        // When a cluster is clicked, zoom into it to reveal the individual markers.
        if (isClusterGraphic(graphic)) {
          const clusterCount = Number(graphic.attributes?.cluster_count);
          onClusterClick?.(clusterCount);
          await zoomIntoCluster(
            viewRef.current,
            graphic.layer as GeoJSONLayer,
            graphic
          );
          return;
        }

        const entityId = graphic.attributes?.id;
        const selectedEntity = entitiesById.get(String(entityId));

        if (!selectedEntity) {
          return;
        }

        const markerMapPoint =
          graphic.geometry?.type === "point"
            ? graphic.geometry
            : event.mapPoint;
        if (!markerMapPoint) {
          return;
        }

        const markerScreenPoint = viewRef.current.toScreen(markerMapPoint);
        if (!markerScreenPoint) {
          return;
        }

        const { placement, horizontalPlacement } = getPopupPlacement(
          markerScreenPoint,
          viewRef.current.width,
          viewRef.current.height
        );

        setSelectedMarkerPopup({
          entity: selectedEntity,
          mapPoint: markerMapPoint,
          x: markerScreenPoint.x,
          y: markerScreenPoint.y,
          placement,
          horizontalPlacement,
        });
      } catch (error) {
        handleErrorWithToast({
          id: `openMapMarkerPopup-${Date.now().toString()}`,
          title: "Failed to open map marker popup.",
          error,
        });
        setSelectedMarkerPopup(null);
      }
    }
  );

  /**
   * Updates cursor style while pointer moves over clickable markers or clusters.
   */
  const handleMapPointerMove = useEffectEvent(async (): Promise<void> => {
    if (!viewRef.current?.container) {
      return;
    }

    if (!latestPointerScreenPoint.current || !isPopupEnabled) {
      viewRef.current.container.style.cursor = "default";
      return;
    }

    if (isPointerHitTestInFlight.current) {
      shouldRunPointerHitTestAgain.current = true;
      return;
    }

    isPointerHitTestInFlight.current = true;

    try {
      const graphic = await getAnyLayerGraphic(
        viewRef.current,
        layerRef.current,
        latestPointerScreenPoint.current
      );
      viewRef.current.container.style.cursor = graphic ? "pointer" : "default";
    } finally {
      isPointerHitTestInFlight.current = false;

      if (shouldRunPointerHitTestAgain.current) {
        shouldRunPointerHitTestAgain.current = false;
        void handleMapPointerMove();
      }
    }
  });

  /**
   * Throttles pointer hit-tests to one run per animation frame.
   */
  const schedulePointerCursorUpdate = useEffectEvent((): void => {
    if (pointerMoveFrameHandle.current !== null) {
      return;
    }

    pointerMoveFrameHandle.current = window.requestAnimationFrame(() => {
      pointerMoveFrameHandle.current = null;
      void handleMapPointerMove();
    });
  });

  // Register map listeners when view is ready and clean them up on dependency change/unmount.
  useEffect(() => {
    if (!isViewReady || !viewRef.current) {
      return;
    }

    // Setup extent watcher for updating popup position during pan/zoom
    extentWatchHandle.current?.remove();
    extentWatchHandle.current = watch(
      () => viewRef.current?.extent,
      () => {
        setSelectedMarkerPopup(
          (currentPopup: SelectedMarkerPopup<T> | null) => {
            if (!currentPopup || !viewRef.current) {
              return currentPopup;
            }

            const markerScreenPoint = viewRef.current.toScreen(
              currentPopup.mapPoint
            );
            if (!markerScreenPoint) {
              return currentPopup;
            }

            const { placement, horizontalPlacement } = getPopupPlacement(
              markerScreenPoint,
              viewRef.current.width,
              viewRef.current.height
            );

            return {
              ...currentPopup,
              x: markerScreenPoint.x,
              y: markerScreenPoint.y,
              placement,
              horizontalPlacement,
            };
          }
        );
      }
    );

    // Setup click handler
    markerClickHandle.current?.remove();
    markerClickHandle.current = viewRef.current.on("click", (event) => {
      void handleMapClick(event);
    });

    // Setup pointer move handler
    markerPointerMoveHandle.current?.remove();
    markerPointerMoveHandle.current = viewRef.current.on(
      "pointer-move",
      (event) => {
        if (!isPopupEnabled) {
          return;
        }

        latestPointerScreenPoint.current = { x: event.x, y: event.y };
        schedulePointerCursorUpdate();
      }
    );

    return () => {
      extentWatchHandle.current?.remove();
      markerClickHandle.current?.remove();
      markerPointerMoveHandle.current?.remove();
      if (pointerMoveFrameHandle.current !== null) {
        window.cancelAnimationFrame(pointerMoveFrameHandle.current);
      }
    };
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewReady, isPopupEnabled, viewRef]);

  // Sync open popup entity with updated store data (e.g. if access level or features change while popup is open)
  useEffect(() => {
    setSelectedMarkerPopup((currentPopup) => {
      if (!currentPopup) {
        return null;
      }

      const updatedEntity = entitiesById.get(currentPopup.entity.id);

      // If the entity no longer exists (e.g. archived), close the popup.
      if (!updatedEntity) {
        return null;
      }

      // Update entity if its reference has changed in the store
      if (updatedEntity !== currentPopup.entity) {
        return {
          ...currentPopup,
          entity: updatedEntity,
        };
      }

      return currentPopup;
    });
  }, [entitiesById]);

  return {
    selectedMarkerPopup,
  };
}
